import { LinearGradient } from 'expo-linear-gradient';
import { memo, useEffect } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import Animated, {
  Extrapolate,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated';
import { landingPageContent } from '../../../constants/Content';
import { COLORS } from '../../../styles/colors';
import AnimatedText from '../../animated/AnimatedText';
import Heading from '../../ui/Heading';
import Card from './Card';

const { mainHeadline, steps } = landingPageContent.howItWorksSection;

// Extract animated card into separate component to avoid hook call inside map
const AnimatedCard = memo(({ step, index, progress }) => {
  const animatedStyle = useAnimatedStyle(() => {
    const currentProgress = progress.value;

    const opacity = interpolate(
      currentProgress,
      [index - 0.5, index - 0.083, index + 0.083, index + 0.5],
      [0, 1, 1, 0],
      Extrapolate.CLAMP
    );

    const scale = interpolate(
      currentProgress,
      [index - 0.5, index - 0.083, index + 0.083, index + 0.5],
      [0.85, 1, 1, 0.85],
      Extrapolate.CLAMP
    );

    const translateY = interpolate(
      currentProgress,
      [index - 0.5, index - 0.083, index + 0.083, index + 0.5],
      [50, 0, 0, -50],
      Extrapolate.CLAMP
    );

    return {
      opacity,
      transform: [{ translateY }, { scale }],
    };
  });

  return (
    <Animated.View style={[styles.cardContainer, animatedStyle]}>
      <Card step={step} />
    </Animated.View>
  );
});

AnimatedCard.displayName = 'AnimatedCard';

const HowItWorksDesktop = ({ scrollY, parentRef }) => {
  const { height: windowHeight } = useWindowDimensions();
  const sectionTop = useSharedValue(0);
  const isLayoutMeasured = useSharedValue(false);

  const PIXELS_PER_STEP = 1200; // 1000px for animation + 200px for dead scroll
  const totalScrollRange = PIXELS_PER_STEP * steps.length;

  useEffect(() => {
    if (parentRef?.current) {
      parentRef.current.measureLayout(
        parentRef.current.getParent?.() || parentRef.current.parentNode,
        (_x, y) => {
          sectionTop.set(y);
          isLayoutMeasured.set(true);
        },
        () => {
          isLayoutMeasured.set(true);
        }
      );
    }
  }, [parentRef, isLayoutMeasured, sectionTop]);

  const handleLayout = (event) => {
    if (!isLayoutMeasured.get()) {
      sectionTop.set(event.nativeEvent.layout.y);
      isLayoutMeasured.set(true);
    }
  };

  const progress = useDerivedValue(() => {
    if (!scrollY || !isLayoutMeasured.value) {
      return 0;
    }

    const sectionStart = sectionTop.value;
    const currentScroll = scrollY.value;

    const viewportBottom = currentScroll + windowHeight;
    if (viewportBottom < sectionStart) {
      return 0;
    }

    const scrolledIntoSection = Math.max(0, currentScroll - sectionStart);
    const progressValue = scrolledIntoSection / PIXELS_PER_STEP;

    return Math.max(0, Math.min(steps.length - 1, progressValue));
  });

  return (
    <LinearGradient
      colors={['#0A1321', '#080808']}
      locations={[0.2058, 1]}
      style={[styles.sectionContainer, { height: windowHeight + totalScrollRange }]}
      onLayout={handleLayout}
    >
      <Animated.View style={[styles.stickyContainer, { height: windowHeight }]}>
        <Heading level="h2" style={styles.mainHeadline} accessibilityRole="header" aria-level="2">
          {mainHeadline}
        </Heading>
        <View style={styles.contentLayout}>
          <View style={styles.leftColumn}>
            <AnimatedText steps={steps} progress={progress} />
          </View>
          <View style={styles.rightColumn}>
            {steps.map((step, index) => (
              <AnimatedCard key={`card-${index}`} step={step} index={index} progress={progress} />
            ))}
          </View>
        </View>
      </Animated.View>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    // backgroundColor is now handled by the gradient
  },
  stickyContainer: {
    position: 'sticky',
    top: 0,
    width: '100%',
    maxWidth: 1200,
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    paddingTop: 200,
    paddingBottom: 40,
  },
  contentLayout: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 0,
    gap: 80,
  },
  leftColumn: {
    flex: 1,
    height: 562,
    justifyContent: 'center',
  },
  rightColumn: {
    flex: 1,
    height: 562,
    justifyContent: 'center',
  },
  mainHeadline: {
    color: COLORS.white,
    textAlign: 'left',
    width: '100%',
    marginBottom: 30,
  },
  cardContainer: {
    position: 'absolute',
  },
});

export default HowItWorksDesktop;
